import { hexlify } from "@/common/lib/utils";
import ApiRequest from "@/common/lib/apiRequest";
import ERC20ABI from "@/common/config/abi/ERC20.json";
import ApolloClient, { gql } from "apollo-boost";

const mutationTypes = {
  SET_AVAILABLE_BALANCE: "SET_AVAILABLE_BALANCE",
  SET_STAKED_BALANCE: "SET_STAKED_BALANCE",
  SET_ALLOWANCE: "SET_ALLOWANCE",
  SET_ACTIVE_EPOCH: "SET_ACTIVE_EPOCH",
  SET_TX_ACTION: "SET_TX_ACTION",
  SET_ESTIMATED_APY: "SET_ESTIMATED_APY",
  SET_STAKED_INFO: "SET_STAKED_INFO",
  SET_PRICES: "SET_PRICES",
  SET_ALLOCATIONS: "SET_ALLOCATIONS",
  RESET_STATE: "RESET_STATE",
};

const state = {
  availableBalance: {
    fetching: false,
    result: null,
    error: null,
  },
  allowance: {
    fetching: false,
    result: null,
    error: null,
  },
  activeEpoch: {
    fetching: false,
    result: null,
    error: null,
  },
  stakedBalance: {
    fetching: false,
    result: null,
    error: null,
  },
  estimatedAPY: {
    fetching: false,
    result: null,
    error: null,
  },
  stakedInfo: {
    fetching: false,
    result: null,
    error: null,
  },
  prices: {
    fetching: false,
    result: null,
    error: null,
  },
  currentTxAction: "",
  allocations: {
    fetching: false,
    result: null,
    error: null,
  },
};

const actions = {
  async fetchActiveEpoch({ commit }) {
    commit(mutationTypes.SET_ACTIVE_EPOCH, { fetching: true });
    try {
      const result = await ApiRequest.get("/eth/thegraph/current_epoch");
      commit(mutationTypes.SET_ACTIVE_EPOCH, {
        result,
        fetching: false,
      });
    } catch (error) {
      commit(mutationTypes.SET_ACTIVE_EPOCH, {
        error,
        fetching: false,
      });
    }
  },
  async fetchStakedBalance({ commit, rootGetters }) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    commit(mutationTypes.SET_STAKED_BALANCE, { fetching: true, result: null });
    try {
      const result = await ApiRequest.get(
        "/eth/thegraph/delegator/delegation/" + walletSelected.address
      );
      commit(mutationTypes.SET_STAKED_BALANCE, { result });
    } catch (error) {
      commit(mutationTypes.SET_STAKED_BALANCE, { error });
    }
  },
  async fetchAvailableBalance({ commit, rootGetters }) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    commit(mutationTypes.SET_AVAILABLE_BALANCE, { fetching: true });
    try {
      const result = await ApiRequest.get(
        "eth/thegraph/delegator/available_balance/" + walletSelected.address
      );
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { result, fetching: false });
    } catch (error) {
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { error, fetching: false });
    }
  },
  async fetchAllowance({ commit, rootGetters }, address) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    address = address || walletSelected?.address;
    if (!address) return;
    commit(mutationTypes.SET_ALLOWANCE, { fetching: true });
    try {
      const result = await ApiRequest.get(
        "eth/thegraph/delegator/allowance/" + address
      );
      if (address.toLowerCase() === walletSelected.address?.toLowerCase()) {
        commit(mutationTypes.SET_ALLOWANCE, { result, fetching: false });
      }
      return result;
    } catch (error) {
      commit(mutationTypes.SET_ALLOWANCE, { error, fetching: false });
    }
  },
  async getApprovalRawTx({ rootGetters }) {
    const { address } = rootGetters["userWallets/walletSelected"];
    const [result, { value: nonce }] = await Promise.all([
      ApiRequest.get(`/eth/thegraph/delegator/digest/approve/${address}`),
      ApiRequest.get(`/eth/thegraph/nonce/${address}`),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getStakeRawTx({ rootGetters }, payload) {
    const { address } = rootGetters["userWallets/walletSelected"];
    const [result, { value: nonce }] = await Promise.all([
      ApiRequest.get(`/eth/thegraph/delegator/digest/delegate/${address}`, {
        params: {
          amount: payload.amount,
        },
      }),
      ApiRequest.get(`/eth/thegraph/nonce/${address}`),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getUnstakeRawTx({ rootGetters }, { amount, ratio }) {
    const { address } = rootGetters["userWallets/walletSelected"];
    const [result, { value: nonce }] = await Promise.all([
      ApiRequest.get(`/eth/thegraph/delegator/digest/undelegate/${address}`, {
        params: {
          amount,
          ratio,
        },
      }),
      ApiRequest.get(`/eth/thegraph/nonce/${address}`),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getWithdrawRawTx({ rootGetters }) {
    const { address } = rootGetters["userWallets/walletSelected"];
    const [result, { value: nonce }] = await Promise.all([
      ApiRequest.get(
        `/eth/thegraph/delegator/digest/withdraw_delegated/${address}`
      ),
      ApiRequest.get(`/eth/thegraph/nonce/${address}`),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getSendRawTx({ rootGetters, dispatch }, payload) {
    const web3 = rootGetters["appConfig/web3"]();
    const walletSelected = rootGetters["userWallets/walletSelected"];
    const getTokenByName = rootGetters["appConfig/getTokenByName"];
    const maxPriorityFeePerGas = rootGetters["appConfig/maxPriorityFeePerGas"];
    const grt = getTokenByName("grt");
    const contract = new web3.eth.Contract(ERC20ABI, grt.contractAddress, {
      from: walletSelected.address,
    });
    const transferMethod = contract.methods.transfer(
      payload.toAddress,
      hexlify(payload.amount)
    );
    const [{ value: nonce }, gasLimit, { fast }] = await Promise.all([
      ApiRequest.get("/eth/thegraph/nonce/" + walletSelected.address),
      transferMethod.estimateGas(),
      dispatch("appConfig/fetchEthGasPrice", null, { root: true }),
    ]);
    const tx = {
      from: walletSelected.address,
      to: grt.contractAddress,
      maxFeePerGas: hexlify(fast * 1e9),
      maxPriorityFeePerGas: hexlify(maxPriorityFeePerGas),
      gas: hexlify(gasLimit),
      value: "0x0",
      nonce: hexlify(nonce),
      data: transferMethod.encodeABI(),
    };
    return tx;
  },
  async broadcastTx({ rootGetters, dispatch }, payload) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    let url;
    let body = {
      type: payload.type,
      metadata: payload.metadata,
    };
    if (payload.signedTx) {
      body.signed_tx = payload.signedTx;
      url = `eth/thegraph/transaction/broadcast/${walletSelected.address}`;
    } else {
      body.tx_hash = payload.txHash;
      url = `eth/thegraph/transaction/create/${walletSelected.address}`;
    }
    return ApiRequest.post(url, body);
  },
  async fetchSwuStakedInfo({ commit, getters }) {
    commit(mutationTypes.SET_STAKED_INFO, { fetching: true });
    try {
      const [totalStaked] = await Promise.all([
        ApiRequest.get("/eth/thegraph/total_staked"),
      ]);
      const result = {
        totalStaked,
      };
      commit(mutationTypes.SET_STAKED_INFO, { result, fetching: false });
    } catch (error) {
      commit(mutationTypes.SET_STAKED_INFO, { error, fetching: false });
    }
  },
  async fetchEstimatedAPY({ commit }) {
    try {
      const result = await ApiRequest.get("/eth/thegraph/apy");
      commit(mutationTypes.SET_ESTIMATED_APY, {
        result: { value: (result?.apy || 0) * 100 },
      });
    } catch (error) {
      commit(mutationTypes.SET_ESTIMATED_APY, { error });
    }
  },
  async fetchAssetPriceChart({ commit }, { startTime, type = "h" } = {}) {
    commit(mutationTypes.SET_PRICES, { fetching: true });
    try {
      startTime = startTime || Date.now() - 86400000;
      startTime = Math.floor(startTime / 1000);
      const data = await ApiRequest.get(
        "/eth/thegraph/grt_prices/" + startTime,
        { params: { type } }
      );
      const result = getPriceData(data, type);
      commit(mutationTypes.SET_PRICES, { result, fetching: false });
      return result;
    } catch (error) {
      commit(mutationTypes.SET_PRICES, { error, fetching: false });
    }
  },
  async fetchAllocations({ commit }) {
    commit(mutationTypes.SET_ALLOCATIONS, { fetching: true });
    const APIURL =
      "https://api.thegraph.com/subgraphs/name/graphprotocol/graph-network-mainnet";
    const INDEXER = "0x7b8f93130b7c610fb102f1e82e23ad978ed81702";
    const tokensQuery = `
      query($id: String) {
        indexer (id: $id) {
          allocations {
            id,
            allocatedTokens,
            createdAt,
            createdAtEpoch,
            subgraphDeployment{
              id,
              originalName,
            }
          }
        }
      }
    `;
    const client = new ApolloClient({
      uri: APIURL,
    });
    try {
      const {
        data: {
          indexer: { allocations: result },
        },
      } = await client.query({
        query: gql(tokensQuery),
        variables: {
          id: INDEXER,
        },
      });
      commit(mutationTypes.SET_ALLOCATIONS, { fetching: false, result });
    } catch (error) {
      commit(mutationTypes.SET_ALLOCATIONS, { fetching: false, error });
    }
  },
  async fetchRequiredInfo({ commit, dispatch }) {
    commit(mutationTypes.SET_ALLOWANCE, { result: null });
    return Promise.all([
      dispatch("fetchAllowance"),
      dispatch("fetchAvailableBalance"),
      dispatch("fetchStakedBalance"),
    ]);
  },
  async fetchNetworkInfo({ dispatch }) {
    await dispatch("fetchActiveEpoch");
    dispatch("fetchSwuStakedInfo");
  },
  setTxAction({ commit }, action) {
    commit(mutationTypes.SET_TX_ACTION, action);
  },
  resetState({ commit }) {
    commit(mutationTypes.RESET_STATE);
  },
};

const mutations = {
  [mutationTypes.SET_AVAILABLE_BALANCE](state, data) {
    state.availableBalance = {
      ...state.availableBalance,
      ...data,
    };
  },
  [mutationTypes.SET_ACTIVE_EPOCH](state, data) {
    state.activeEpoch = {
      ...state.activeEpoch,
      fetching: false,
      ...data,
    };
  },
  [mutationTypes.SET_STAKED_BALANCE](state, data) {
    state.stakedBalance = data;
  },
  [mutationTypes.SET_ALLOWANCE](state, data) {
    state.allowance = {
      ...state.allowance,
      ...data,
    };
  },
  [mutationTypes.SET_TX_ACTION](state, action) {
    state.currentTxAction = action;
  },
  [mutationTypes.SET_STAKED_INFO](state, data) {
    state.stakedInfo = {
      ...state.stakedInfo,
      ...data,
    };
  },
  [mutationTypes.SET_ESTIMATED_APY](state, data) {
    state.estimatedAPY = data;
  },
  [mutationTypes.SET_PRICES](state, data) {
    state.prices = data;
  },
  [mutationTypes.SET_ALLOCATIONS](state, data) {
    state.allocations = data;
  },
  [mutationTypes.RESET_STATE](state) {
    state.availableBalance = {
      fetching: false,
      result: null,
      error: null,
    };
    state.stakedBalance = {
      fetching: false,
      result: null,
      error: null,
    };
    state.currentTxAction = "";
  },
};

const getPriceData = (data, type) => {
  data = data.sort((a, b) => a.timestamp - b.timestamp);
  const result = [];
  for (let index = data.length - 1; index > -1; index -= 1) {
    const elem = data[index];
    result.unshift([elem.timestamp, Number(elem.usd)]);
  }
  return result;
};

const getters = {
  currentTxAction: state => state.currentTxAction,
  availableBalance: state => state.availableBalance,
  stakedBalance: state => state.stakedBalance,
  needUnlockAssets: state => {
    return state.allowance.result?.value <= 0 ?? false;
  },
  activeEpoch: state => state.activeEpoch,
  estimatedAPY: state => state.estimatedAPY,
  stakedInfo: state => state.stakedInfo,
  prices: state => state.prices,
  allocations: state => state.allocations,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
